import { Box, HStack, Text, VStack, useToast, type UseToastOptions } from '@chakra-ui/react';
import { Button, Modal, Notice } from '../../../../ui';
import { inviteLinkFor, type InviteEmployeeResult } from './useYaxleTeam';

export interface InviteHandoff {
    email: string;
    /** Used in the copy; falls back to the email for invites saved without a name. */
    name: string;
    result: InviteEmployeeResult;
}

/** True when something has to be given to the invitee by hand rather than by email. */
export const needsHandoff = (result: InviteEmployeeResult): boolean =>
    !!result.microsoft365?.temporaryPassword || !!result.mailboxPending || !result.emailSent;

/** What the invite did in Microsoft 365, plus anything it couldn't do. */
const microsoft365Summary = (result: InviteEmployeeResult): string[] => {
    const m365 = result.microsoft365;
    if (!m365) return [];
    const warnings = m365.warnings ?? [];
    const done = [
        m365.accountCreated && 'account created',
        m365.licensesAssigned > 0 && 'license assigned',
        m365.groupsJoined > 0 && 'added to the employee group',
    ].filter(Boolean);
    const summary = done.length > 0 ? `Microsoft 365: ${done.join(', ')}.` : warnings.length === 0 ? 'Microsoft 365 was already set up.' : null;
    return [summary, ...warnings].filter((part): part is string => !!part);
};

/** Toast for a sent (or resent) invite that needs nothing handed over. */
export const inviteToastOptions = (email: string, result: InviteEmployeeResult): UseToastOptions => ({
    status: result.microsoft365?.warnings?.length ? 'warning' : 'success',
    title: `Invite sent to ${email}`,
    description: microsoft365Summary(result).join(' ') || undefined,
    duration: 6000,
    isClosable: true,
});

interface CopyRowProps {
    label: string;
    value: string;
    isSecret?: boolean;
}

const CopyRow = ({ label, value, isSecret = false }: CopyRowProps) => {
    const toast = useToast();
    const copy = () =>
        navigator.clipboard.writeText(value).then(
            () => toast({ status: 'success', title: `${label} copied`, duration: 2000 }),
            () => toast({ status: 'error', title: `Couldn't copy the ${label.toLowerCase()}`, isClosable: true }),
        );
    return (
        <HStack spacing={3} align="center" py={2} borderBottomWidth="1px" borderColor="border.subtle">
            <Text textStyle="bodySm" color="fg.muted" w="150px" flexShrink={0}>
                {label}
            </Text>
            <Text
                flex={1}
                minW={0}
                textStyle={isSecret ? 'body' : 'bodySm'}
                fontFamily={isSecret ? 'mono' : undefined}
                color="fg.default"
                wordBreak="break-all"
            >
                {value}
            </Text>
            <Button size="xs" variant="ghostGray" onClick={copy}>
                Copy
            </Button>
        </HStack>
    );
};

export interface InviteHandoffModalProps {
    handoff: InviteHandoff | null;
    onClose: () => void;
}

/**
 * Everything to give an invitee by hand: a new Microsoft 365 account's one-time temporary
 * password, and the Yaxle invite link when it couldn't be emailed. Only Done closes it, so
 * the password can't be dismissed by accident.
 */
const InviteHandoffModal = ({ handoff, onClose }: InviteHandoffModalProps) => {
    if (!handoff) return null;
    const { email, name, result } = handoff;
    const password = result.microsoft365?.temporaryPassword ?? null;
    const warnings = result.microsoft365?.warnings ?? [];
    const showLink = !!result.inviteId && (!!result.mailboxPending || !result.emailSent);
    return (
        <Modal
            isOpen
            onClose={onClose}
            size="2xl"
            title={`Invite ready for ${name}`}
            closeOnOverlayClick={false}
            closeOnEsc={false}
            showCloseButton={false}
            footer={<Button onClick={onClose}>Done</Button>}
        >
            <VStack align="stretch" spacing={6} pb={2}>
                {password && (
                    <Box>
                        <Text textStyle="labelSm" color="fg.default">
                            Microsoft 365 account created
                        </Text>
                        <Text textStyle="bodySm" color="fg.muted" mb={2}>
                            Give {name} these in person or by phone. They choose a new password the first time they sign
                            in at office.com. This password is shown only once.
                        </Text>
                        <CopyRow label="Sign-in" value={email} />
                        <CopyRow label="Temporary password" value={password} isSecret />
                    </Box>
                )}
                {showLink && result.inviteId && (
                    <Box>
                        <Text textStyle="labelSm" color="fg.default">
                            Yaxle invite link
                        </Text>
                        <Text textStyle="bodySm" color="fg.muted" mb={2}>
                            {result.mailboxPending
                                ? "Their mailbox is still being set up, so we didn't email the invite. Send them this link, or use Resend on the team page in a few minutes."
                                : "The invite email didn't send. Send them this link instead."}
                        </Text>
                        <CopyRow label="Link" value={inviteLinkFor(result.inviteId)} />
                    </Box>
                )}
                {warnings.length > 0 && (
                    <Notice tone="warning" title="Microsoft 365 didn't finish setting them up">
                        {`${warnings.join(' ')} Resend from the team page tries again.`}
                    </Notice>
                )}
            </VStack>
        </Modal>
    );
};

export default InviteHandoffModal;
